import React, { FormEvent, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { API_BASE_URL } from '../../config/api';
import { useGetUserWalletBalanceQuery } from '../../store/slices/apiSlice';
import { Link } from 'react-router-dom';
import { 
  Sprout, 
  Users, 
  CheckCircle2, 
  Clock, 
  ShieldCheck, 
  CreditCard, 
  Wallet, 
  ChevronRight,
  ChevronLeft,
  AlertCircle, 
  Gift, 
  Truck, 
  ArrowRight, 
  RefreshCw,
  Plus,
  Trash2,
  PieChart,
  Calendar,
  MapPin,
  Check,
  X
} from 'lucide-react';

interface FarmProject {
  farm_project_id: number;
  title: string;
  short_description?: string;
  full_description?: string;
  location: string | null;
  image_url?: string | null;
  images?: string[];
  estimated_harvest_at: string | null;
  capacity_remaining: number;
  benefit_options: ('produce' | 'rita_credit')[];
  max_beneficiaries_per_sponsorship: number;
}

interface Beneficiary {
  beneficiary_id?: number;
  full_name: string;
  phone_e164: string;
  allocation_percentage: number;
  verification_status?: 'unverified' | 'pending' | 'verified' | 'failed';
}

interface Sponsorship {
  public_id: string;
  status: string;
  benefit_type: 'produce' | 'rita_credit' | null;
  sponsorship_amount: number | null;
  currency_code: string | null;
  created_at: string;
  paid_at?: string;
  farm_project?: {
    title: string;
    location: string | null;
    estimated_harvest_at: string | null;
  };
  beneficiaries: Beneficiary[];
}

interface ApiResponse<T> {
  success: boolean;
  message: string;
  data: T;
}

interface FarmCardImageSliderProps {
  images: string[];
  title: string;
  getImageUrl: (url: string | null | undefined) => string;
}

const FarmCardImageSlider: React.FC<FarmCardImageSliderProps> = ({ images, title, getImageUrl }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!images || images.length === 0) return null;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div className="w-full h-44 rounded-xl overflow-hidden mb-3 border border-gray-100 shadow-sm relative bg-white flex items-center justify-center p-2 group">
      <img
        src={getImageUrl(images[currentIndex])}
        alt={`${title} - photo ${currentIndex + 1}`}
        className="w-full h-full object-contain transition-all duration-300"
      />
      <span className="absolute top-2 left-2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-600/90 text-white backdrop-blur-xs text-xs font-bold shadow-md z-10">
        <Sprout className="w-3.5 h-3.5" /> Active Farm
      </span>

      {images.length > 1 && (
        <>
          {/* Previous Arrow */}
          <button
            type="button"
            onClick={handlePrev}
            className="absolute left-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white backdrop-blur-xs transition-all z-10 hover:scale-110 shadow-md"
            title="Previous Image"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>

          {/* Next Arrow */}
          <button
            type="button"
            onClick={handleNext}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full bg-black/60 hover:bg-black/80 text-white backdrop-blur-xs transition-all z-10 hover:scale-110 shadow-md"
            title="Next Image"
          >
            <ChevronRight className="w-4 h-4" />
          </button>

          {/* Dots Indicator Bar */}
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 z-10 bg-black/50 px-2.5 py-1 rounded-full backdrop-blur-xs">
            {images.map((_, idx) => (
              <button
                key={idx}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentIndex(idx);
                }}
                className={`h-2 rounded-full transition-all ${
                  currentIndex === idx ? 'w-5 bg-emerald-400' : 'w-2 bg-white/70 hover:bg-white'
                }`}
                title={`Go to image ${idx + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

const FarmSponsorship: React.FC = () => {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.token);
  const user = useSelector((state: RootState) => state.auth.user);

  // Helper to format image URLs
  const getImageUrl = (url: string | null | undefined) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      // If the image is hosted on production but we are running locally, redirect to local backend so local uploads work
      if (API_BASE_URL.includes('localhost') && url.includes('diasporawise.com')) {
        return url.replace(/https?:\/\/diasporawise\.com(:\d+)?/, API_BASE_URL);
      }
      return url;
    }
    if (url.startsWith('/uploads')) {
      return `${API_BASE_URL}${url}`;
    }
    return `${API_BASE_URL}/uploads/${url}`;
  };

  // Active Tab: 'sponsor' | 'my_sponsorships'
  const [activeTab, setActiveTab] = useState<'sponsor' | 'my_sponsorships'>('sponsor');

  // Checkout Wizard Step: 1 = Choose Farm, 2 = Benefit & Amount, 3 = Beneficiaries, 4 = Payment Review, 5 = Complete
  const [step, setStep] = useState<number>(1);

  const [farms, setFarms] = useState<FarmProject[]>([]);
  const [selectedFarm, setSelectedFarm] = useState<FarmProject | null>(null);
  const [detailsFarm, setDetailsFarm] = useState<FarmProject | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState<number>(0);
  const [benefitType, setBenefitType] = useState<'produce' | 'rita_credit'>('produce');
  const [amount, setAmount] = useState('500');
  const [currencyCode, setCurrencyCode] = useState('USD');
  const [capacity, setCapacity] = useState('1');
  const paymentMethod = 'wallet';

  const [beneficiaries, setBeneficiaries] = useState<Beneficiary[]>([
    { full_name: '', phone_e164: '+234', allocation_percentage: 100 }
  ]);

  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sponsorships, setSponsorships] = useState<Sponsorship[]>([]);
  const [otpValues, setOtpValues] = useState<Record<number, string>>({});
  const [loadingOtp, setLoadingOtp] = useState<Record<number, boolean>>({});

  const isUserLoggedIn = Boolean(token && user && user.role === 'USER');
  const isAdminLoggedIn = Boolean(token && user && (user.role === 'AU' || user.role === 'SU'));

  const { data: balanceData, isLoading: isLoadingBalance } = useGetUserWalletBalanceQuery(undefined, {
    skip: !isUserLoggedIn,
  });



  const walletBalance = balanceData?.data?.wallet_balance || 0;
  // API returns currency_symbol directly (e.g. ₹, $, £) — use it as-is
  const currencySymbol = balanceData?.data?.currency_symbol || '$';
  // walletCurrency is the symbol itself (used in display)
  const walletCurrency = currencySymbol;

  const getIsoCurrencyCode = (symbolOrCode: string | undefined): string => {
    if (!symbolOrCode) return 'USD';
    const clean = symbolOrCode.trim();
    if (clean.length === 3 && /^[A-Za-z]{3}$/.test(clean)) {
      return clean.toUpperCase();
    }
    switch (clean) {
      case '₹': return 'INR';
      case '$': return 'USD';
      case '£': return 'GBP';
      case '€': return 'EUR';
      case '₦': return 'NGN';
      case 'CA$': return 'CAD';
      case 'A$': return 'AUD';
      default: return 'USD';
    }
  };

  // Auto-detect and lock currency ISO code to logged-in user's wallet
  useEffect(() => {
    if (isUserLoggedIn && currencySymbol) {
      setCurrencyCode(getIsoCurrencyCode(currencySymbol));
    }
  }, [isUserLoggedIn, currencySymbol]);

  const totalAllocation = useMemo(() => {
    return beneficiaries.reduce((sum, item) => sum + Number(item.allocation_percentage || 0), 0);
  }, [beneficiaries]);

  const isBeneficiariesValid = useMemo(() => {
    if (beneficiaries.length === 0) return false;
    return beneficiaries.every(
      (b) => b.full_name.trim().length > 0 && b.phone_e164.trim().length >= 8
    );
  }, [beneficiaries]);

  const handleProceedToPayment = () => {
    if (!isUserLoggedIn) {
      setMessage({
        type: 'error',
        text: isAdminLoggedIn
          ? 'You are currently logged in as an Admin. Farm sponsorship is exclusively for registered User accounts. Please log in as a User.'
          : 'Please log in to your User account to sponsor a farm project using your wallet.'
      });
      navigate('/user-login?redirect=/farm-sponsorship');
      return;
    }

    if (!isBeneficiariesValid) {
      setMessage({
        type: 'error',
        text: 'Please enter a valid full name and phone number for all beneficiaries before proceeding.'
      });
      return;
    }

    if (Math.abs(totalAllocation - 100) > 0.01) {
      setMessage({
        type: 'error',
        text: 'Total beneficiary allocation percentage must equal exactly 100%.'
      });
      return;
    }

    setMessage(null);
    setStep(4);
  };

  useEffect(() => {
    void fetch(`${API_BASE_URL}/api/farm-projects`)
      .then(async (response) => response.json() as Promise<ApiResponse<FarmProject[]>>)
      .then((response) => {
        const data = response.data ?? [];
        setFarms(data);
        if (data.length > 0) {
          setSelectedFarm(data[0]);
          if (data[0].benefit_options?.length > 0) {
            setBenefitType(data[0].benefit_options[0]);
          }
        }
      })
      .catch(() => setMessage({ type: 'error', text: 'Failed to load available farm projects.' }));
  }, []);

  const loadSponsorships = async () => {
    if (!token) {
      setSponsorships([]);
      return;
    }
    try {
      const response = await fetch(`${API_BASE_URL}/api/farm-sponsorships`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const payload = (await response.json()) as ApiResponse<Sponsorship[]>;
      if (!payload.success) throw new Error(payload.message);
      setSponsorships(payload.data ?? []);
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Sponsorship history could not be loaded.'
      });
    }
  };

  useEffect(() => {
    void loadSponsorships();
  }, [token]);

  const updateBeneficiary = (index: number, field: keyof Beneficiary, value: string | number) => {
    setBeneficiaries((items) =>
      items.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]: field === 'allocation_percentage' ? Number(value) : value
            }
          : item
      )
    );
  };

  const addBeneficiary = () => {
    if (selectedFarm && beneficiaries.length >= selectedFarm.max_beneficiaries_per_sponsorship) return;
    const newCount = beneficiaries.length + 1;
    const equalShare = Number((100 / newCount).toFixed(2));
    const updated = beneficiaries.map((b) => ({ ...b, allocation_percentage: equalShare }));
    const remainder = Number((100 - equalShare * newCount).toFixed(2));
    updated.push({
      full_name: '',
      phone_e164: '+234',
      allocation_percentage: Number((equalShare + remainder).toFixed(2))
    });
    setBeneficiaries(updated);
  };

  const removeBeneficiary = (index: number) => {
    if (beneficiaries.length === 1) return;
    const remaining = beneficiaries.filter((_, itemIndex) => itemIndex !== index);
    const equalShare = Number((100 / remaining.length).toFixed(2));
    const remainder = Number((100 - equalShare * remaining.length).toFixed(2));
    setBeneficiaries(
      remaining.map((b, idx) => ({
        ...b,
        allocation_percentage: idx === 0 ? Number((equalShare + remainder).toFixed(2)) : equalShare
      }))
    );
  };

  const equalizeAllocations = () => {
    if (beneficiaries.length === 0) return;
    const equalShare = Number((100 / beneficiaries.length).toFixed(2));
    const remainder = Number((100 - equalShare * beneficiaries.length).toFixed(2));
    setBeneficiaries(
      beneficiaries.map((b, idx) => ({
        ...b,
        allocation_percentage: idx === 0 ? Number((equalShare + remainder).toFixed(2)) : equalShare
      }))
    );
  };

  const handleCheckoutSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!isUserLoggedIn) {
      setMessage({
        type: 'error',
        text: isAdminLoggedIn
          ? 'You are currently logged in as an Admin. Farm Sponsorship requires a regular User account to use user wallet.'
          : 'Please log in to your User account to complete farm sponsorship using your wallet.'
      });
      navigate('/user-login?redirect=/farm-sponsorship');
      return;
    }
    if (!selectedFarm) {
      setMessage({ type: 'error', text: 'Please select a farm project.' });
      return;
    }
    if (!isBeneficiariesValid) {
      setMessage({ type: 'error', text: 'Please fill in a valid full name and phone number for all beneficiaries.' });
      return;
    }
    if (Math.abs(totalAllocation - 100) > 0.01) {
      setMessage({ type: 'error', text: 'Total beneficiary allocation must equal exactly 100%.' });
      return;
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };
      
      // 1. Create Draft
      const draftResponse = await fetch(`${API_BASE_URL}/api/farm-sponsorships/drafts`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ farm_project_id: selectedFarm.farm_project_id })
      });
      const draft = (await draftResponse.json()) as ApiResponse<{ public_id: string }>;
      if (!draft.success) throw new Error(draft.message);

      // 2. Update Draft details & beneficiaries
      const updateResponse = await fetch(`${API_BASE_URL}/api/farm-sponsorships/${draft.data.public_id}/draft`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          benefit_type: benefitType,
          sponsorship_amount: Number(amount),
          currency_code: getIsoCurrencyCode(currencyCode),
          capacity_reserved: Number(capacity),
          beneficiaries
        })
      });
      const update = (await updateResponse.json()) as ApiResponse<unknown>;
      if (!update.success) throw new Error(update.message);

      // 3. Confirm Payment (Wallet)
      const paymentResponse = await fetch(`${API_BASE_URL}/api/farm-sponsorships/${draft.data.public_id}/payments/wallet`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ idempotency_key: crypto.randomUUID() })
      });
      const payment = (await paymentResponse.json()) as ApiResponse<unknown>;
      if (!payment.success) throw new Error(payment.message);

      setMessage({
        type: 'success',
        text: '🎉 Congratulations! Your farm sponsorship has been confirmed. Produce or Rita.ng credit will be delivered at harvest time!'
      });
      setStep(5);
      await loadSponsorships();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error instanceof Error ? error.message : 'Unable to complete sponsorship payment.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestVerification = async (sponsorship: Sponsorship, beneficiary: Beneficiary) => {
    if (!token || !beneficiary.beneficiary_id) return;
    const bId = beneficiary.beneficiary_id;
    setLoadingOtp((prev) => ({ ...prev, [bId]: true }));
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/farm-sponsorships/${sponsorship.public_id}/beneficiaries/${bId}/verification/send`,
        { method: 'POST', headers: { Authorization: `Bearer ${token}` } }
      );
      const payload = (await response.json()) as ApiResponse<unknown>;
      if (!payload.success) throw new Error(payload.message);
      setMessage({ type: 'info', text: `Verification OTP sent to ${beneficiary.full_name} (${beneficiary.phone_e164}).` });
      await loadSponsorships();
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Unable to send OTP.' });
    } finally {
      setLoadingOtp((prev) => ({ ...prev, [bId]: false }));
    }
  };

  const confirmVerification = async (sponsorship: Sponsorship, beneficiary: Beneficiary) => {
    if (!token || !beneficiary.beneficiary_id) return;
    const bId = beneficiary.beneficiary_id;
    const otp = otpValues[bId];
    if (!otp || otp.length !== 6) {
      setMessage({ type: 'error', text: 'Please enter a valid 6-digit OTP code.' });
      return;
    }
    setLoadingOtp((prev) => ({ ...prev, [bId]: true }));
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/farm-sponsorships/${sponsorship.public_id}/beneficiaries/${bId}/verification/confirm`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ otp })
        }
      );
      const payload = (await response.json()) as ApiResponse<unknown>;
      if (!payload.success) throw new Error(payload.message);
      setMessage({ type: 'success', text: `✅ Beneficiary ${beneficiary.full_name} is successfully verified!` });
      await loadSponsorships();
    } catch (error) {
      setMessage({ type: 'error', text: error instanceof Error ? error.message : 'Invalid OTP verification code.' });
    } finally {
      setLoadingOtp((prev) => ({ ...prev, [bId]: false }));
    }
  };

  return (
    <div className="min-h-screen bg-gray-50/50 pb-16 pt-6">
      {/* Header Banner */}
      <header className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-8">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-emerald-800 via-emerald-700 to-green-900 text-white p-8 sm:p-12 shadow-xl">
          <div className="absolute -right-10 -bottom-10 opacity-10 pointer-events-none">
            <Sprout className="w-96 h-96" />
          </div>
          <div className="relative z-10 max-w-3xl">
            <div className="flex flex-wrap items-center gap-3 mb-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 backdrop-blur-md border border-emerald-400/30 text-emerald-200 text-xs font-semibold uppercase tracking-wider">
                <Gift className="w-3.5 h-3.5" /> Diaspora Impact Module
              </div>

              {isUserLoggedIn && (
                <div className="inline-flex items-center gap-2 px-3.5 py-1 rounded-full bg-emerald-950/70 backdrop-blur-md border border-emerald-400/40 text-white text-xs font-semibold shadow-md">
                  <Wallet className="w-3.5 h-3.5 text-emerald-400" />
                  <span>Wallet:</span>
                  <span className="font-extrabold text-emerald-300">
                    {isLoadingBalance
                      ? 'Loading...'
                      : `${currencySymbol}${walletBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${walletCurrency}`}
                  </span>
                </div>
              )}
            </div>
            <h1 className="text-3xl sm:text-5xl font-extrabold tracking-tight text-white mb-4">
              Farm Project Sponsorship
            </h1>
            <p className="text-lg sm:text-xl text-emerald-100/90 leading-relaxed">
              Sponsor agricultural projects in Nigeria on behalf of family members & loved ones. At harvest, they receive fresh farm produce or redeemable Rita.ng shopping credits!
            </p>

            <div className="mt-8 flex flex-wrap gap-4 items-center">
              <button
                onClick={() => setActiveTab('sponsor')}
                className={`px-6 py-3 rounded-xl font-bold transition-all shadow-md flex items-center gap-2 ${
                  activeTab === 'sponsor'
                    ? 'bg-white text-emerald-900 hover:bg-emerald-50'
                    : 'bg-emerald-800/60 text-emerald-100 hover:bg-emerald-800 border border-emerald-600/50'
                }`}
              >
                <Sprout className="w-5 h-5" /> Sponsor a Farm
              </button>
              <button
                onClick={() => setActiveTab('my_sponsorships')}
                className={`px-6 py-3 rounded-xl font-bold transition-all shadow-md flex items-center gap-2 ${
                  activeTab === 'my_sponsorships'
                    ? 'bg-white text-emerald-900 hover:bg-emerald-50'
                    : 'bg-emerald-800/60 text-emerald-100 hover:bg-emerald-800 border border-emerald-600/50'
                }`}
              >
                <Users className="w-5 h-5" /> My Sponsorships ({sponsorships.length})
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Login Prompt Banner for unauthenticated visitors or Admin role */}
        {!isUserLoggedIn && (
          <div className="mb-6 bg-gradient-to-r from-amber-500 to-amber-600 text-white p-4 sm:p-5 rounded-2xl shadow-md flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 backdrop-blur-xs rounded-xl">
                <Wallet className="w-6 h-6 text-white" />
              </div>
              <div>
                <h4 className="font-extrabold text-sm sm:text-base">
                  {isAdminLoggedIn ? 'Admin Account Logged In — User Login Required' : 'User Login Required for Wallet Payment'}
                </h4>
                <p className="text-xs text-amber-100 mt-0.5">
                  {isAdminLoggedIn
                    ? 'You are currently logged in as an Admin. Farm Sponsorship is exclusively for registered User accounts using their user wallet.'
                    : 'Please sign in to your Diasporawise User account to sponsor farm projects using your wallet balance.'}
                </p>
              </div>
            </div>
            <button
              onClick={() => navigate('/user-login?redirect=/farm-sponsorship')}
              className="w-full sm:w-auto px-5 py-2.5 bg-white text-amber-900 font-extrabold text-xs rounded-xl shadow-sm hover:bg-amber-50 transition-all text-center shrink-0"
            >
              {isAdminLoggedIn ? 'Switch to User Login' : 'Sign In as User'}
            </button>
          </div>
        )}

        {/* Global Notification Banner */}
        {message && (
          <div
            className={`mb-6 p-4 rounded-2xl flex items-center gap-3 shadow-sm border ${
              message.type === 'success'
                ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                : message.type === 'error'
                ? 'bg-rose-50 border-rose-200 text-rose-800'
                : 'bg-blue-50 border-blue-200 text-blue-800'
            }`}
          >
            {message.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 flex-shrink-0 text-emerald-600" />
            ) : message.type === 'error' ? (
              <AlertCircle className="w-5 h-5 flex-shrink-0 text-rose-600" />
            ) : (
              <ShieldCheck className="w-5 h-5 flex-shrink-0 text-blue-600" />
            )}
            <p className="text-sm font-medium flex-1">{message.text}</p>
            <button
              onClick={() => setMessage(null)}
              className="text-xs font-semibold opacity-70 hover:opacity-100 underline ml-auto"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* TAB 1: SPONSOR A FARM (CHECKOUT WIZARD) */}
        {activeTab === 'sponsor' && (
          <div className="grid lg:grid-cols-12 gap-8">
            {/* Left Column: Multi-Step Flow */}
            <div className="lg:col-span-8 space-y-6">
              {/* Stepper Progress Header */}
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex justify-between items-center overflow-x-auto gap-4">
                {[
                  { stepNum: 1, label: 'Select Farm' },
                  { stepNum: 2, label: 'Benefit & Amount' },
                  { stepNum: 3, label: 'Beneficiaries' },
                  { stepNum: 4, label: 'Payment' }
                ].map((s) => (
                  <div key={s.stepNum} className="flex items-center gap-2 flex-shrink-0">
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        step === s.stepNum
                          ? 'bg-emerald-600 text-white ring-4 ring-emerald-100'
                          : step > s.stepNum
                          ? 'bg-emerald-100 text-emerald-700'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {step > s.stepNum ? <Check className="w-4 h-4" /> : s.stepNum}
                    </div>
                    <span
                      className={`text-sm font-semibold hidden md:inline ${
                        step === s.stepNum ? 'text-gray-900' : 'text-gray-400'
                      }`}
                    >
                      {s.label}
                    </span>
                    {s.stepNum < 4 && <ChevronRight className="w-4 h-4 text-gray-300 ml-2" />}
                  </div>
                ))}
              </div>

              {/* STEP 1: Select Farm Project */}
              {step === 1 && (
                <div className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Step 1: Select a Farm Project</h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Choose an active farming project to sponsor in Nigeria.
                    </p>
                  </div>

                  {farms.length === 0 ? (
                    <div className="py-12 text-center text-gray-500">
                      <Sprout className="w-12 h-12 mx-auto text-gray-300 mb-3 animate-pulse" />
                      Loading active farm projects...
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-4">
                      {farms.map((farm) => {
                        const isSelected = selectedFarm?.farm_project_id === farm.farm_project_id;
                        return (
                          <div
                            key={farm.farm_project_id}
                            onClick={() => {
                              setSelectedFarm(farm);
                              if (farm.benefit_options?.length > 0) setBenefitType(farm.benefit_options[0]);
                            }}
                            className={`cursor-pointer rounded-2xl p-5 border-2 transition-all relative overflow-hidden flex flex-col justify-between ${
                              isSelected
                                ? 'border-emerald-600 bg-emerald-50/40 shadow-md'
                                : 'border-gray-100 hover:border-emerald-200 bg-white'
                            }`}
                          >
                            <div>
                              {(() => {
                                const projectImages = farm.images && farm.images.length > 0 
                                  ? farm.images 
                                  : (farm.image_url ? [farm.image_url] : []);
                                return (
                                  <FarmCardImageSlider
                                    images={projectImages}
                                    title={farm.title}
                                    getImageUrl={getImageUrl}
                                  />
                                );
                              })()}
                              <div className="flex justify-between items-start mb-2">
                                {(!farm.images || farm.images.length === 0) && !farm.image_url && (
                                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
                                    <Sprout className="w-3.5 h-3.5" /> Active Farm
                                  </span>
                                )}
                                {isSelected && (
                                  <CheckCircle2 className="w-5 h-5 text-emerald-600 ml-auto flex-shrink-0" />
                                )}
                              </div>
                              <h3 className="text-lg font-bold text-gray-900 mb-1">{farm.title}</h3>
                              {farm.short_description && (
                                <p className="text-xs text-gray-500 mb-3 line-clamp-2 leading-relaxed">{farm.short_description}</p>
                              )}
                              <div className="flex items-center justify-between mb-3">
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setDetailsFarm(farm);
                                    setActiveImageIndex(0);
                                  }}
                                  className="text-xs font-bold text-emerald-600 hover:text-emerald-800 hover:underline inline-flex items-center gap-1 focus:outline-hidden"
                                >
                                  View Details & Photos &rarr;
                                </button>
                              </div>
                              <div className="space-y-1.5 text-xs text-gray-600 mb-4">
                                <div className="flex items-center gap-1.5">
                                  <MapPin className="w-3.5 h-3.5 text-emerald-600" />
                                  <span className="font-semibold text-gray-700">{farm.location ?? 'Nigeria'}</span>
                                </div>
                                {farm.estimated_harvest_at && (
                                  <div className="flex items-center gap-1.5">
                                    <Calendar className="w-3.5 h-3.5 text-amber-600" />
                                    <span>Harvest: {new Date(farm.estimated_harvest_at).toLocaleDateString()}</span>
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="pt-3 border-t border-gray-100 flex justify-between items-center text-xs">
                              <span className="text-gray-500">Remaining Slots:</span>
                              <span className="font-bold text-emerald-700">{farm.capacity_remaining} units</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  <div className="flex justify-end pt-4">
                    <button
                      disabled={!selectedFarm}
                      onClick={() => setStep(2)}
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      Next: Benefit & Amount <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 2: Benefit & Amount */}
              {step === 2 && selectedFarm && (
                <div className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Step 2: Benefit Type & Sponsorship Amount</h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Choose how your beneficiaries will receive farm benefits at harvest.
                    </p>
                  </div>

                  {/* Benefit Selection Cards */}
                  <div className="space-y-3">
                    <label className="text-sm font-bold text-gray-700">Choose Harvest Benefit Option:</label>
                    <div className="grid sm:grid-cols-2 gap-4">
                      {selectedFarm.benefit_options.map((opt) => {
                        const isOptSelected = benefitType === opt;
                        return (
                          <div
                            key={opt}
                            onClick={() => setBenefitType(opt)}
                            className={`cursor-pointer rounded-2xl p-5 border-2 transition-all flex items-start gap-4 ${
                              isOptSelected
                                ? 'border-emerald-600 bg-emerald-50/40 shadow-sm'
                                : 'border-gray-100 hover:border-gray-200 bg-white'
                            }`}
                          >
                            <div
                              className={`p-3 rounded-xl ${
                                isOptSelected ? 'bg-emerald-600 text-white' : 'bg-gray-100 text-gray-600'
                              }`}
                            >
                              {opt === 'produce' ? (
                                <Truck className="w-6 h-6" />
                              ) : (
                                <Gift className="w-6 h-6" />
                              )}
                            </div>
                            <div>
                              <h4 className="font-bold text-gray-900 text-base mb-1">
                                {opt === 'produce' ? 'Physical Farm Produce' : 'Rita.ng Shopping Credit'}
                              </h4>
                              <p className="text-xs text-gray-500 leading-relaxed">
                                {opt === 'produce'
                                  ? 'Harvested crops (Yam, Maize, Tomatoes, Rice, etc.) delivered directly to your loved ones.'
                                  : 'Beneficiaries receive shopping credit redeemable for groceries on Rita.ng.'}
                              </p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Amount Inputs */}
                  <div className="grid sm:grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Sponsorship Amount ({currencyCode}):
                      </label>
                      <div className="relative">
                        <input
                          type="number"
                          min="1"
                          step="0.01"
                          required
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          className="w-full pl-4 pr-16 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-bold text-gray-900"
                        />
                        <div className="absolute right-3 top-3 text-xs font-bold text-gray-400 uppercase">
                          {currencyCode}
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-1">
                        Currency:
                      </label>
                      {isUserLoggedIn ? (
                        <div className="w-full px-4 py-3 border border-emerald-300 bg-emerald-50/60 rounded-xl font-extrabold text-emerald-900 flex items-center justify-between shadow-2xs">
                          <div className="flex items-center gap-2">
                            <Wallet className="w-4 h-4 text-emerald-600" />
                            <span>{currencyCode} ({currencySymbol})</span>
                          </div>
                          <span className="text-[10px] bg-emerald-200 text-emerald-800 px-2 py-0.5 rounded-full font-bold uppercase">
                            Account Wallet Currency
                          </span>
                        </div>
                      ) : (
                        <select
                          value={currencyCode}
                          onChange={(e) => setCurrencyCode(e.target.value.toUpperCase())}
                          className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 font-bold text-gray-900 bg-white"
                        >
                          <option value="USD">USD ($)</option>
                          <option value="EUR">EUR (€)</option>
                          <option value="GBP">GBP (£)</option>
                          <option value="NGN">NGN (₦)</option>
                          <option value="CAD">CAD (CA$)</option>
                          <option value="AUD">AUD (A$)</option>
                        </select>
                      )}
                      {isUserLoggedIn && (
                        <p className="text-[11px] text-gray-500 mt-1">
                          Auto-detected from your account wallet balance.
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between pt-4">
                    <button
                      onClick={() => setStep(1)}
                      className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={() => setStep(3)}
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md transition-all flex items-center gap-2"
                    >
                      Next: Beneficiaries <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 3: Beneficiaries & Allocation Splitter */}
              {step === 3 && selectedFarm && (
                <div className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6">
                  <div className="flex flex-wrap justify-between items-start gap-4">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Step 3: Nominate Beneficiaries</h2>
                      <p className="text-gray-500 text-sm mt-1">
                        Add up to {selectedFarm.max_beneficiaries_per_sponsorship} beneficiaries and divide harvest percentage.
                      </p>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={equalizeAllocations}
                        className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-lg transition-all flex items-center gap-1"
                      >
                        <PieChart className="w-3.5 h-3.5" /> Equalize Split
                      </button>
                      <button
                        type="button"
                        onClick={addBeneficiary}
                        disabled={beneficiaries.length >= selectedFarm.max_beneficiaries_per_sponsorship}
                        className="px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 text-xs font-bold rounded-lg transition-all flex items-center gap-1 disabled:opacity-50"
                      >
                        <Plus className="w-3.5 h-3.5" /> Add Beneficiary
                      </button>
                    </div>
                  </div>

                  {/* Total Percentage Split Bar */}
                  <div className="bg-gray-50 p-4 rounded-xl border border-gray-200 space-y-2">
                    <div className="flex justify-between text-xs font-bold">
                      <span className="text-gray-700">Total Allocation Total:</span>
                      <span
                        className={
                          Math.abs(totalAllocation - 100) < 0.01
                            ? 'text-emerald-600 font-extrabold'
                            : 'text-rose-600 font-extrabold'
                        }
                      >
                        {totalAllocation.toFixed(2)}% / 100%
                      </span>
                    </div>
                    <div className="w-full h-3 bg-gray-200 rounded-full overflow-hidden flex">
                      {beneficiaries.map((b, idx) => (
                        <div
                          key={idx}
                          style={{ width: `${Math.max(0, Math.min(100, b.allocation_percentage))}%` }}
                          className={`h-full ${
                            idx % 4 === 0
                              ? 'bg-emerald-500'
                              : idx % 4 === 1
                              ? 'bg-teal-500'
                              : idx % 4 === 2
                              ? 'bg-green-500'
                              : 'bg-emerald-700'
                          }`}
                          title={`${b.full_name || `Beneficiary #${idx + 1}`}: ${b.allocation_percentage}%`}
                        />
                      ))}
                    </div>
                  </div>

                  {/* Beneficiary List Form Inputs */}
                  <div className="space-y-4">
                    {beneficiaries.map((b, idx) => (
                      <div
                        key={idx}
                        className="p-4 rounded-xl border border-gray-100 bg-gray-50/50 space-y-3 relative group"
                      >
                        <div className="flex justify-between items-center text-xs font-bold text-gray-500">
                          <span>Beneficiary #{idx + 1}</span>
                          {beneficiaries.length > 1 && (
                            <button
                              type="button"
                              onClick={() => removeBeneficiary(idx)}
                              className="text-rose-500 hover:text-rose-700 p-1"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          )}
                        </div>

                        <div className="grid sm:grid-cols-12 gap-3">
                          <div className="sm:col-span-5">
                            <label className="block text-xs font-bold text-gray-600 mb-1">Full Name:</label>
                            <input
                              type="text"
                              required
                              placeholder="e.g. Femi Adewumi"
                              value={b.full_name}
                              onChange={(e) => updateBeneficiary(idx, 'full_name', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>

                          <div className="sm:col-span-4">
                            <label className="block text-xs font-bold text-gray-600 mb-1">
                              Phone Number (E.164 format):
                            </label>
                            <input
                              type="text"
                              required
                              placeholder="+2348012345678"
                              value={b.phone_e164}
                              onChange={(e) => updateBeneficiary(idx, 'phone_e164', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>

                          <div className="sm:col-span-3">
                            <label className="block text-xs font-bold text-gray-600 mb-1">Allocation %:</label>
                            <input
                              type="number"
                              min="1"
                              max="100"
                              step="0.01"
                              required
                              value={b.allocation_percentage}
                              onChange={(e) => updateBeneficiary(idx, 'allocation_percentage', e.target.value)}
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-semibold focus:ring-2 focus:ring-emerald-500"
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="flex justify-between pt-4">
                    <button
                      onClick={() => setStep(2)}
                      className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleProceedToPayment}
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md transition-all flex items-center gap-2"
                    >
                      Next: Review & Payment <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}

              {/* STEP 4: Payment Review */}
              {step === 4 && selectedFarm && (
                <form
                  onSubmit={handleCheckoutSubmit}
                  className="bg-white p-6 sm:p-8 rounded-2xl border border-gray-100 shadow-sm space-y-6"
                >
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Step 4: Review & Payment</h2>
                    <p className="text-gray-500 text-sm mt-1">
                      Confirm sponsorship breakdown and authorize payment.
                    </p>
                  </div>

                  {/* Payment Summary Box */}
                  <div className="bg-emerald-50/50 p-6 rounded-2xl border border-emerald-100 space-y-4">
                    <div className="flex justify-between items-center border-b border-emerald-100 pb-3">
                      <span className="text-sm font-bold text-emerald-900">Selected Farm Project:</span>
                      <span className="text-sm font-extrabold text-emerald-900">{selectedFarm.title}</span>
                    </div>
                    <div className="flex justify-between items-center border-b border-emerald-100 pb-3">
                      <span className="text-sm font-bold text-emerald-900">Benefit Type:</span>
                      <span className="text-sm font-extrabold text-emerald-900 uppercase">
                        {benefitType === 'produce' ? 'Physical Produce Delivery' : 'Rita.ng Shopping Credit'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center border-b border-emerald-100 pb-3">
                      <span className="text-sm font-bold text-emerald-900">Total Beneficiaries:</span>
                      <span className="text-sm font-extrabold text-emerald-900">{beneficiaries.length} person(s)</span>
                    </div>
                    <div className="flex justify-between items-center pt-2">
                      <span className="text-base font-bold text-gray-900">Total Amount:</span>
                      <span className="text-2xl font-extrabold text-emerald-700">
                        {amount} {currencyCode}
                      </span>
                    </div>
                  </div>

                  {/* Payment Method Display */}
                  <div className="space-y-3">
                    <label className="block text-sm font-bold text-gray-700">Payment Method:</label>
                    <div className="p-4 sm:p-5 rounded-2xl border-2 border-emerald-600 bg-emerald-50/40 shadow-sm space-y-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-3 bg-emerald-600 text-white rounded-xl shadow-xs">
                            <Wallet className="w-6 h-6" />
                          </div>
                          <div>
                            <p className="font-extrabold text-sm text-gray-900">Diasporawise Account Wallet</p>
                            <p className="text-xs text-gray-500">Deducted directly from your account balance</p>
                          </div>
                        </div>
                        <span className="px-3 py-1 bg-emerald-100 text-emerald-800 font-extrabold text-xs rounded-full uppercase">
                          Active Wallet
                        </span>
                      </div>

                      <div className="pt-3 border-t border-emerald-100/80 flex items-center justify-between">
                        <span className="text-xs font-bold text-gray-600">Your Current Wallet Balance:</span>
                        <span className={`text-base font-extrabold ${walletBalance < Number(amount) ? 'text-rose-600' : 'text-emerald-800'}`}>
                          {isLoadingBalance
                            ? 'Loading...'
                            : `${currencySymbol}${walletBalance.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${walletCurrency}`}
                        </span>
                      </div>

                      {walletBalance < Number(amount) && (
                        <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-semibold flex items-center justify-between">
                          <span>⚠️ Wallet balance is lower than {amount} {currencyCode}.</span>
                          <Link to="/user/wallet" className="underline text-rose-900 font-extrabold hover:text-rose-950 ml-2">
                            Top Up Wallet &rarr;
                          </Link>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex justify-between pt-4 border-t border-gray-100">
                    <button
                      type="button"
                      onClick={() => setStep(3)}
                      className="px-6 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 font-bold rounded-xl transition-all"
                    >
                      Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="px-8 py-3.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold rounded-xl shadow-lg transition-all flex items-center gap-2 disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" /> Processing Payment...
                        </>
                      ) : (
                        <>
                          Confirm & Pay {amount} {currencyCode} <ShieldCheck className="w-5 h-5" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}

              {/* STEP 5: Confirmation Success */}
              {step === 5 && (
                <div className="bg-white p-8 sm:p-12 rounded-2xl border border-gray-100 shadow-sm text-center space-y-6">
                  <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center mx-auto text-emerald-600">
                    <CheckCircle2 className="w-12 h-12" />
                  </div>
                  <div className="max-w-md mx-auto">
                    <h2 className="text-3xl font-extrabold text-gray-900">Sponsorship Confirmed!</h2>
                    <p className="text-gray-600 text-sm mt-2 leading-relaxed">
                      Your farm sponsorship has been logged. Your nominated beneficiaries will receive their updates as the harvest date approaches.
                    </p>
                  </div>

                  <div className="flex justify-center gap-4 pt-4">
                    <button
                      onClick={() => {
                        setStep(1);
                        setActiveTab('my_sponsorships');
                      }}
                      className="px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-xl shadow-md transition-all flex items-center gap-2"
                    >
                      View Sponsorship Tracking Dashboard <ArrowRight className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Right Column: Information & Assistance Sidebar */}
            <div className="lg:col-span-4 space-y-6">
              <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
                <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" /> How Farm Sponsorship Works
                </h3>
                <ol className="space-y-3 text-xs text-gray-600 leading-relaxed list-decimal pl-4">
                  <li>
                    <strong>Nominate Beneficiaries:</strong> Add up to 10 family members or friends in Nigeria.
                  </li>
                  <li>
                    <strong>OTP Phone Verification:</strong> Beneficiaries verify their Nigerian mobile number.
                  </li>
                  <li>
                    <strong>Harvest Distribution:</strong> At harvest time, produce is delivered or Rita.ng credit is issued.
                  </li>
                </ol>
              </div>

              <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-6 rounded-2xl border border-emerald-100 text-emerald-900 space-y-3">
                <h4 className="font-bold text-sm flex items-center gap-2">
                  <Gift className="w-4 h-4 text-emerald-600" /> Rita.ng Integration
                </h4>
                <p className="text-xs text-emerald-800 leading-relaxed">
                  Rita.ng shopping credit allows beneficiaries to buy fresh foodstuffs, groceries, and household goods online for instant local delivery!
                </p>
              </div>
            </div>
          </div>
        )}

        {/* TAB 2: MY SPONSORED FARMS & BENEFICIARY TRACKING */}
        {activeTab === 'my_sponsorships' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900">My Farm Sponsorships</h2>
                <p className="text-gray-500 text-sm mt-1">
                  Track harvest progress and manage beneficiary phone verifications.
                </p>
              </div>
              <button
                onClick={() => void loadSponsorships()}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl transition-all flex items-center gap-1.5"
              >
                <RefreshCw className="w-3.5 h-3.5" /> Refresh List
              </button>
            </div>

            {sponsorships.length === 0 ? (
              <div className="bg-white p-12 rounded-2xl border border-gray-100 text-center space-y-4">
                <Sprout className="w-12 h-12 mx-auto text-gray-300" />
                <h3 className="text-lg font-bold text-gray-700">No Farm Sponsorships Yet</h3>
                <p className="text-xs text-gray-500 max-w-sm mx-auto">
                  You haven't sponsored any farm projects yet. Click below to start your first farm sponsorship!
                </p>
                <button
                  onClick={() => setActiveTab('sponsor')}
                  className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-xs rounded-xl shadow-md transition-all inline-flex items-center gap-2"
                >
                  Sponsor a Farm Now <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="space-y-6">
                {sponsorships
                  .filter((s) => s.status !== 'draft')
                  .map((s) => {
                    const projTitle = s.farm_project?.project?.title || s.farm_project?.title || 'Farm Project';
                    const projLocation = s.farm_project?.project?.location || s.farm_project?.location;

                    return (
                      <div
                        key={s.public_id}
                        className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-6"
                      >
                        {/* Card Header */}
                        <div className="flex flex-wrap justify-between items-start gap-4 border-b border-gray-100 pb-4">
                          <div>
                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold mb-2">
                              {s.benefit_type === 'rita_credit' ? 'Rita.ng Shopping Credit' : 'Farm Produce Delivery'}
                            </div>
                            <h3 className="text-xl font-extrabold text-gray-900 flex items-center gap-2">
                              <Sprout className="w-5 h-5 text-emerald-600" /> {projTitle}
                            </h3>
                            {projLocation && (
                              <p className="text-xs text-emerald-700 font-bold mt-1 flex items-center gap-1">
                                <MapPin className="w-3.5 h-3.5" /> Location: {projLocation}
                              </p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                              ID: <code className="font-mono">{s.public_id}</code> | Created:{' '}
                              {new Date(s.created_at).toLocaleDateString()}
                            </p>
                          </div>

                          <div className="text-right">
                            <span className="inline-block px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs font-extrabold uppercase tracking-wide border border-emerald-200">
                              Status: {s.status}
                            </span>
                            <p className="text-lg font-extrabold text-gray-900 mt-1">
                              {s.sponsorship_amount ?? 0} {s.currency_code ?? ''}
                            </p>
                          </div>
                        </div>

                    {/* Beneficiaries Breakdown & OTP controls */}
                    <div className="space-y-3">
                      <h4 className="text-sm font-bold text-gray-800 flex items-center gap-2">
                        <Users className="w-4 h-4 text-emerald-600" /> Beneficiaries ({s.beneficiaries.length})
                      </h4>

                      <div className="grid sm:grid-cols-2 gap-4">
                        {s.beneficiaries.map((b) => {
                          const bId = b.beneficiary_id;
                          const isVerified = b.verification_status === 'verified';
                          const isLoading = bId ? loadingOtp[bId] : false;

                          return (
                            <div
                              key={b.beneficiary_id || b.full_name}
                              className="p-4 rounded-xl border border-gray-100 bg-gray-50/60 space-y-3"
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h5 className="font-bold text-sm text-gray-900">{b.full_name}</h5>
                                  <p className="text-xs text-gray-500">{b.phone_e164}</p>
                                </div>
                                <span
                                  className={`px-2.5 py-0.5 rounded-full text-[10px] font-extrabold uppercase ${
                                    isVerified
                                      ? 'bg-emerald-100 text-emerald-800'
                                      : b.verification_status === 'pending'
                                      ? 'bg-amber-100 text-amber-800'
                                      : 'bg-rose-100 text-rose-800'
                                  }`}
                                >
                                  {b.verification_status ?? 'unverified'}
                                </span>
                              </div>

                              <div className="flex justify-between items-center text-xs text-gray-600 border-t border-gray-200/60 pt-2">
                                <span>Harvest Allocation:</span>
                                <span className="font-bold text-emerald-700">{b.allocation_percentage}%</span>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                );
              })}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Farm Project Details Modal */}
      {detailsFarm && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl max-w-2xl w-full p-6 sm:p-8 space-y-6 shadow-2xl overflow-y-auto max-h-[90vh] text-left relative animate-in fade-in zoom-in-95 duration-200">
            {/* Close Button */}
            <button
              onClick={() => setDetailsFarm(null)}
              className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-all text-gray-400 hover:text-gray-700"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Title & Badges */}
            <div className="space-y-2">
              <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
                <Sprout className="w-3.5 h-3.5" /> Active Farm Project
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-gray-900 tracking-tight leading-tight pr-8">
                {detailsFarm.title}
              </h2>
              {detailsFarm.location && (
                <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-bold">
                  <MapPin className="w-3.5 h-3.5" />
                  <span>{detailsFarm.location}</span>
                </div>
              )}
            </div>

            {/* Image Gallery / Carousel Section */}
            {detailsFarm.images && detailsFarm.images.length > 0 && (
              <div className="space-y-3">
                {/* Main Large Image */}
                <div className="w-full h-64 sm:h-80 rounded-2xl overflow-hidden border border-gray-150 shadow-sm relative bg-white flex items-center justify-center p-2">
                  <img
                    src={getImageUrl(detailsFarm.images[activeImageIndex])}
                    alt={`${detailsFarm.title} - Photo ${activeImageIndex + 1}`}
                    className="w-full h-full object-contain transition-all duration-350"
                  />
                  <span className="absolute bottom-3 right-3 px-3 py-1 rounded-lg bg-black/60 backdrop-blur-md text-white text-[11px] font-extrabold shadow-sm">
                    {activeImageIndex + 1} / {detailsFarm.images.length}
                  </span>
                </div>

                {/* Thumbnails list */}
                {detailsFarm.images.length > 1 && (
                  <div className="flex gap-2 overflow-x-auto pb-1 max-w-full">
                    {detailsFarm.images.map((imgUrl, idx) => (
                      <button
                        key={idx}
                        onClick={() => setActiveImageIndex(idx)}
                        className={`w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 border-2 transition-all relative ${
                          activeImageIndex === idx ? 'border-emerald-600 scale-95 shadow-sm' : 'border-gray-200 opacity-60 hover:opacity-100'
                        }`}
                      >
                        <img src={getImageUrl(imgUrl)} alt="thumbnail" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Description & Operational Details */}
            <div className="space-y-3">
              <h3 className="text-base font-extrabold text-gray-900 border-b border-gray-100 pb-1.5">
                About the Farm Operations
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed font-medium whitespace-pre-line">
                {detailsFarm.full_description || detailsFarm.short_description || "No detailed operational breakdown available for this project yet."}
              </p>
            </div>

            {/* Farm Specifications Grid */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-2xl border border-gray-100 text-xs font-bold">
              <div>
                <p className="text-gray-400 uppercase tracking-wider text-[10px]">Harvest Date</p>
                <p className="text-gray-800 text-sm mt-0.5">
                  {detailsFarm.estimated_harvest_at
                    ? new Date(detailsFarm.estimated_harvest_at).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' })
                    : 'Awaiting harvest'}
                </p>
              </div>

              <div>
                <p className="text-gray-400 uppercase tracking-wider text-[10px]">Remaining Slots</p>
                <p className="text-emerald-700 text-sm mt-0.5">{detailsFarm.capacity_remaining} Units</p>
              </div>

              <div>
                <p className="text-gray-400 uppercase tracking-wider text-[10px]">Nominees Limit</p>
                <p className="text-gray-800 text-sm mt-0.5">{detailsFarm.max_beneficiaries_per_sponsorship} Beneficiaries</p>
              </div>

              <div>
                <p className="text-gray-400 uppercase tracking-wider text-[10px]">Supported Benefits</p>
                <div className="flex gap-1 mt-1">
                  {detailsFarm.benefit_options.map((opt) => (
                    <span
                      key={opt}
                      className={`px-1.5 py-0.5 rounded-full text-[9px] font-extrabold uppercase ${
                        opt === 'produce' ? 'bg-emerald-100 text-emerald-800' : 'bg-purple-100 text-purple-800'
                      }`}
                    >
                      {opt === 'produce' ? '🌿 Produce' : '🛒 Credit'}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setDetailsFarm(null)}
                className="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-bold rounded-xl"
              >
                Close Details
              </button>
              <button
                type="button"
                onClick={() => {
                  setSelectedFarm(detailsFarm);
                  if (detailsFarm.benefit_options?.length > 0) {
                    setBenefitType(detailsFarm.benefit_options[0]);
                  }
                  setDetailsFarm(null);
                  setStep(2); // Go directly to Step 2
                }}
                className="px-6 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-extrabold text-xs rounded-xl shadow-md transition-all flex items-center gap-1.5"
              >
                <CheckCircle2 className="w-4 h-4" /> Sponsor This Farm
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmSponsorship;
